
import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles } from 'lucide-react';
import { useToast } from "@/components/ui/use-toast";

interface AIDocumentGeneratorProps {
  onResultGenerated: (result: string) => void;
}

const AIDocumentGenerator: React.FC<AIDocumentGeneratorProps> = ({ onResultGenerated }) => {
  const [documentType, setDocumentType] = useState("minutes");
  const [documentPrompt, setDocumentPrompt] = useState("");
  const [generatingDocument, setGeneratingDocument] = useState(false);
  const { toast } = useToast();

  const handleGenerateDocument = () => {
    if (!documentPrompt.trim()) {
      toast({
        title: "Input required",
        description: "Please provide details for document generation",
        variant: "destructive"
      });
      return;
    }

    setGeneratingDocument(true);
    
    // Simulate document generation with timeout
    setTimeout(() => {
      let generatedText = "";
      
      if (documentType === "minutes") {
        generatedText = `# MINUTES OF THE MEETING\n\nDate: ${new Date().toLocaleDateString()}\n\n## ATTENDEES\n- Board Members present as per attendance register\n\n## AGENDA\n1. ${documentPrompt}\n\n## DISCUSSIONS\nThe board discussed in detail about ${documentPrompt}.\n\n## RESOLUTIONS\nAfter careful consideration, the board RESOLVED THAT:\n\n1. The company shall proceed with ${documentPrompt}\n2. The management is authorized to take necessary steps to implement the above resolution\n\n## CONCLUSION\nThere being no other business, the meeting concluded with a vote of thanks to the Chair.`;
      } else if (documentType === "resolution") {
        generatedText = `# BOARD RESOLUTION\n\nRESOLVED THAT pursuant to the provisions of the Companies Act, 2013 and rules made thereunder, the consent of the Board be and is hereby accorded to ${documentPrompt}.\n\nRESOLVED FURTHER THAT the Directors of the Company be and are hereby authorized to sign and execute all necessary documents, forms, and returns as may be required to give effect to this resolution.\n\nFor [COMPANY NAME]\n\nDirector\nDIN: _____________`;
      } else {
        generatedText = `# E-FORM DRAFT\n\nThis document contains the necessary information to be filled in the required e-form related to ${documentPrompt}.\n\n## PARTICULARS TO BE FILLED\n\n1. Company details: [COMPANY NAME], [CIN]\n2. Purpose: ${documentPrompt}\n3. Date of event: ${new Date().toLocaleDateString()}\n\n## ATTACHMENTS REQUIRED\n\n1. Board Resolution approving ${documentPrompt}\n2. Additional supporting documents as applicable`;
      }
      
      onResultGenerated(generatedText);
      setGeneratingDocument(false);
      
      toast({
        title: "Document generated",
        description: "Your document was generated successfully",
      });
    }, 2000);
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          AI Document Generation
        </CardTitle>
        <CardDescription>
          Generate legal documents and drafts using AI assistance
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <div className="mb-2 text-sm font-medium">Document Type</div>
              <div className="flex flex-col space-y-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="documentType"
                    value="minutes"
                    checked={documentType === "minutes"}
                    onChange={() => setDocumentType("minutes")}
                    className="h-4 w-4 text-primary"
                  />
                  <span>Minutes of Meeting</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="documentType"
                    value="resolution"
                    checked={documentType === "resolution"}
                    onChange={() => setDocumentType("resolution")}
                    className="h-4 w-4 text-primary"
                  />
                  <span>Board Resolution</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="documentType"
                    value="eform"
                    checked={documentType === "eform"}
                    onChange={() => setDocumentType("eform")}
                    className="h-4 w-4 text-primary"
                  />
                  <span>E-Form Draft</span>
                </label>
              </div>
            </div>
            <div className="md:col-span-2">
              <div className="mb-2 text-sm font-medium">Document Instructions</div>
              <Textarea
                placeholder={
                  documentType === "minutes"
                    ? "Describe the meeting purpose, key agenda items, and decisions made..."
                    : documentType === "resolution"
                    ? "Describe the resolution needed, e.g., 'approve opening of a current account with HDFC Bank'..."
                    : "Describe the filing purpose, e.g., 'appointment of a new director'..."
                }
                className="h-24"
                value={documentPrompt}
                onChange={(e) => setDocumentPrompt(e.target.value)}
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button 
              className="gap-2" 
              onClick={handleGenerateDocument}
              disabled={generatingDocument}
            >
              <Sparkles className="h-4 w-4" />
              {generatingDocument ? "Generating..." : "Generate Document"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default AIDocumentGenerator;
